export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">About This Tool</h1>
          <p className="text-lg text-gray-600">
            Understanding what we are—and what we're not
          </p>
        </div>

        {/* Non-Medical Disclaimer */}
        <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-red-800 mb-4">⚠️ Important Disclaimer</h2>
          <p className="text-red-900 mb-4">
            This tool is <strong>NOT a substitute for professional mental health care</strong>. It is not designed to diagnose, treat, or cure any mental health condition.
          </p>
          <p className="text-red-900 mb-4">
            If you are experiencing a mental health crisis, suicidal thoughts, or severe distress, please reach out to:
          </p>
          <ul className="text-red-900 space-y-2 mb-4">
            <li>• National Suicide Prevention Lifeline: 988 (US)</li>
            <li>• Crisis Text Line: Text HOME to 741741</li>
            <li>• International Association for Suicide Prevention: https://www.iasp.info/resources/Crisis_Centres/</li>
            <li>• Your local emergency services: 911 (US) or equivalent</li>
          </ul>
          <p className="text-red-900 font-semibold">
            This tool is for emotional awareness and general well-being only.
          </p>
        </div>

        {/* What We Are */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">✨ What We Are</h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <span className="text-2xl">💭</span>
              <div>
                <h3 className="font-bold text-gray-800">Emotional Awareness Tool</h3>
                <p className="text-gray-600">We help you pause and reflect on your feelings in a judgment-free space.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="text-2xl">💬</span>
              <div>
                <h3 className="font-bold text-gray-800">Supportive Companion</h3>
                <p className="text-gray-600">We offer empathetic responses and practical coping suggestions.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="text-2xl">📊</span>
              <div>
                <h3 className="font-bold text-gray-800">Pattern Tracker</h3>
                <p className="text-gray-600">We help you see trends in your mood and stress to build awareness.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="text-2xl">🧠</span>
              <div>
                <h3 className="font-bold text-gray-800">CBT-Inspired Guide</h3>
                <p className="text-gray-600">We offer thought reframing exercises based on cognitive behavioral principles.</p>
              </div>
            </div>
          </div>
        </div>

        {/* What We're Not */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">❌ What We're NOT</h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <span className="text-2xl">🚫</span>
              <div>
                <h3 className="font-bold text-gray-800">Not Therapy</h3>
                <p className="text-gray-600">We don't replace professional therapy or counseling.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="text-2xl">🚫</span>
              <div>
                <h3 className="font-bold text-gray-800">Not Diagnosis</h3>
                <p className="text-gray-600">We don't diagnose mental health conditions or disorders.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="text-2xl">🚫</span>
              <div>
                <h3 className="font-bold text-gray-800">Not Treatment</h3>
                <p className="text-gray-600">We don't provide medical treatment or clinical interventions.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="text-2xl">🚫</span>
              <div>
                <h3 className="font-bold text-gray-800">Not a Crisis Service</h3>
                <p className="text-gray-600">We're not equipped to handle mental health emergencies.</p>
              </div>
            </div>
          </div>
        </div>

        {/* MoSCoW Prioritization */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">🎯 How We Built This (MoSCoW Framework)</h2>

          <div className="space-y-6">
            {/* Must Have */}
            <div className="border-l-4 border-red-500 pl-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3">🔴 Must Have (Core MVP)</h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Mood input (5 options)</li>
                <li>✓ Stress scale (1-10)</li>
                <li>✓ Empathetic AI response</li>
                <li>✓ CBT micro exercise (when stress high)</li>
              </ul>
            </div>

            {/* Should Have */}
            <div className="border-l-4 border-yellow-500 pl-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3">🟡 Should Have (Enhanced UX)</h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Session history tracking</li>
                <li>✓ Analytics summary (avg stress, mood frequency)</li>
                <li>✓ Multi-page navigation</li>
              </ul>
            </div>

            {/* Could Have */}
            <div className="border-l-4 border-blue-500 pl-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3">🔵 Could Have (Future Enhancements)</h3>
              <ul className="space-y-2 text-gray-700">
                <li>○ Mood trend charts</li>
                <li>○ Streak counter</li>
                <li>○ Export check-in summaries</li>
                <li>○ Dark mode</li>
              </ul>
            </div>

            {/* Won't Have */}
            <div className="border-l-4 border-gray-500 pl-6">
              <h3 className="text-xl font-bold text-gray-800 mb-3">⚫ Won't Have (Out of Scope)</h3>
              <ul className="space-y-2 text-gray-700">
                <li>✗ User authentication</li>
                <li>✗ Backend database</li>
                <li>✗ Diagnosis features</li>
                <li>✗ Therapy replacement</li>
                <li>✗ Clinical treatment</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-600">
          <p>Built with care for emotional awareness. Not a substitute for professional help.</p>
        </div>
      </div>
    </div>
  );
}
